import apiClient from '../client';

// Types
export interface AuditStatistics {
  success: boolean;
  statistics: {
    period_days: number;
    audit_events: {
      total: number;
      high_severity: number;
      daily_average: number;
    };
    security_events: {
      total: number;
      unresolved: number;
      resolution_rate: number;
    };
    compliance_health: {
      score: number;
      status: string;
    };
    event_types: {
      [key: string]: number;
    };
    severity_distribution: {
      [key: string]: number;
    };
  };
}

export interface AuditLog {
  id: number;
  event_id: string;
  event_type: string;
  severity: string;
  timestamp: string;
  user_id?: string;
  session_id?: string;
  ip_address?: string;
  user_agent?: string;
  resource_type?: string;
  resource_id?: string;
  action: string;
  description: string;
  before_data?: any;
  after_data?: any;
  event_metadata?: any;
  compliance_tags?: string[];
}

export interface AuditReport {
  report_id: string;
  report_type: string;
  period_start: string;
  period_end: string;
  generated_at: string;
  total_events: number;
  summary: any;
  events: AuditLog[];
}

// API Functions
export const auditService = {
  // Get audit statistics
  getStatistics: async (days: number = 30): Promise<AuditStatistics> => {
    const response = await apiClient.get(`/audit/statistics?days=${days}`);
    return response.data;
  },

  // Get audit logs (uses /events endpoint)
  getLogs: async (params?: {
    event_type?: string;
    severity?: string;
    user_id?: string;
    resource_type?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ logs: AuditLog[]; total: number }> => {
    const response = await apiClient.get('/audit/events', { params });
    return response.data;
  },

  // Search audit logs
  searchLogs: async (query: string, params?: {
    event_type?: string;
    severity?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ logs: AuditLog[]; total: number }> => {
    const response = await apiClient.get('/audit/search', {
      params: { q: query, ...params },
    });
    return response.data;
  },

  // Get audit log by ID
  getLogById: async (eventId: string): Promise<{ success: boolean; log: AuditLog }> => {
    const response = await apiClient.get(`/audit/logs/${eventId}`);
    return response.data;
  },

  // Get user activity
  getUserActivity: async (userId: string, params?: {
    days?: number;
    skip?: number;
    limit?: number;
  }): Promise<{ logs: AuditLog[]; total: number }> => {
    const response = await apiClient.get(`/audit/user/${userId}/activity`, { params });
    return response.data;
  },

  // Get resource history
  getResourceHistory: async (resourceType: string, resourceId: string, params?: {
    skip?: number;
    limit?: number;
  }): Promise<{ logs: AuditLog[]; total: number }> => {
    const response = await apiClient.get(`/audit/resource/${resourceType}/${resourceId}/history`, { params });
    return response.data;
  },

  // Generate audit report
  generateReport: async (reportData: {
    report_type: string;
    start_date: string;
    end_date: string;
    event_types?: string[];
    severity_levels?: string[];
    include_details?: boolean;
  }): Promise<{ success: boolean; report: AuditReport }> => {
    const response = await apiClient.post('/audit/reports/generate', reportData);
    return response.data;
  },

  // Export audit logs
  exportLogs: async (format: string, params?: {
    event_type?: string;
    severity?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> => {
    const response = await apiClient.get('/audit/export', {
      params: { format, ...params },
      responseType: 'blob',
    });
    return response.data;
  },

  // Get compliance summary
  getComplianceSummary: async (days: number = 30): Promise<{
    success: boolean;
    summary: {
      compliance_score: number;
      status: string;
      frameworks: any[];
      audit_coverage: number;
      gaps_identified: number;
      recommendations: string[];
    };
  }> => {
    const response = await apiClient.get(`/audit/compliance-summary?days=${days}`);
    return response.data;
  },

  // Get event types
  getEventTypes: async (): Promise<{ success: boolean; event_types: string[] }> => {
    const response = await apiClient.get('/audit/event-types');
    return response.data;
  },

  // Verify log integrity
  verifyIntegrity: async (eventId: string): Promise<{
    success: boolean;
    verified: boolean;
    checksum_match: boolean;
    details: any;
  }> => {
    const response = await apiClient.post(`/audit/logs/${eventId}/verify`);
    return response.data;
  },
};

export default auditService;

